import { ComponentType } from 'react';
import { ReactComponent as SignalIcon } from 'icons/signal.svg';
import { ReactComponent as PayinsIcon } from 'icons/arrow-down.svg';
import { ReactComponent as PayoutsIcon } from 'icons/arrow-up.svg';
import { ReactComponent as ItemsIcon } from 'icons/payments.svg';
import { ReactComponent as TransfersIcon } from 'icons/repeat.svg';
import { ReactComponent as UsersIcon } from 'icons/users.svg';
import { ReactComponent as AccountIcon } from 'icons/account.svg';
import { ReactComponent as GettingStartedIcon } from 'icons/check-all.svg';
import { ReactComponent as ReportsIcon } from 'icons/file.svg';
import type { FeatureFlag } from 'context/FeaturesContext/FeaturesContext';
import type { Environment } from 'types';
import { PAYMENT_TABS, PaymentTabSlug } from 'views/Payments/paymentsTabs';

export interface NavItem {
  readonly label: string;
  readonly to: string;
  readonly icon: ComponentType;
  readonly flag?: FeatureFlag;
  readonly environment?: Environment;
  /** Not offered on the en-GB locale; filled from `PaymentTab.hiddenForGBR`. */
  readonly hiddenForGBR?: boolean;
}

export interface NavGroupConfig {
  readonly label?: string;
  readonly items: readonly NavItem[];
}

/** One icon per dataset. Keyed by slug, so a new tab can't ship without one. */
const PAYMENT_TAB_ICONS: Record<PaymentTabSlug, ComponentType> = {
  payins: PayinsIcon,
  payouts: PayoutsIcon,
  items: ItemsIcon,
  transfers: TransfersIcon,
};

/**
 * Money movement mirrors the Payments datasets, so their slugs, labels and
 * gating (GBR, sub-wallets) are defined once, in `paymentsTabs.ts`.
 */
const paymentItems: readonly NavItem[] = PAYMENT_TABS.map((tab) => ({
  label: tab.label,
  to: `/payments/${tab.slug}`,
  icon: PAYMENT_TAB_ICONS[tab.slug],
  hiddenForGBR: tab.hiddenForGBR,
  flag: tab.requiresSubWallets ? ('subWallets' as const) : undefined,
}));

export const navConfig: readonly NavGroupConfig[] = [
  {
    items: [
      {
        label: 'Getting Started',
        to: '/getting-started',
        icon: GettingStartedIcon,
        environment: 'pre-live',
      },
      { label: 'Overview', to: '/', icon: SignalIcon },
    ],
  },
  {
    label: 'Money movement',
    items: paymentItems,
  },
  {
    label: 'Accounts',
    items: [{ label: 'Users', to: '/users', icon: UsersIcon }],
  },
  {
    label: 'Platform',
    items: [
      {
        label: 'Reports',
        to: '/reports',
        icon: ReportsIcon,
        flag: 'data-export',
      },
      {
        label: 'Platform Profile',
        to: '/platform-profile',
        icon: AccountIcon,
      },
    ],
  },
];

export default navConfig;
